<template>
  <div class="">
    <ul class="flex flex-wrap items-center gap-1">
      <li v-for="aggregatedTag in aggregatedOwnTags" :key="aggregatedTag.key" class="relative">
        <TagBadge :can-delete="true"
                  @delete="deleteAggregatedTag(aggregatedTag)"
                  :entity-id="entityId"
                  :tag-name="aggregatedTag.tagName"
                  :tag-type="aggregatedTag.tagType"
                  :tag-color="aggregatedTag.tagColor"
                  :tag-relationships="aggregatedTag.relationships"
                  :tag-relationship-count="aggregatedTag.count" />
      </li>
      <li v-if="descendantTagSummary.length > 0">
        <button
          @click="showDescendantModal = true"
          class="flex items-center gap-1 text-xs px-2 py-1 border border-dashed border-gray-300 rounded-full text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-all duration-200"
        >
          <FontAwesomeIcon :icon="['fas','sitemap']" style="height: 10px" />
          {{ totalDescendantTagCount }} descendant {{ totalDescendantTagCount === 1 ? 'tag' : 'tags' }}
        </button>
      </li>
      <li>
        <FontAwesomeIcon @click="showAddTagForm=true" class="cursor-pointer text-gray-400 hover:text-gray-600" style="height: 16px" :icon="['fas','circle-plus']"/>
      </li>
    </ul>
  </div>

  <Form_Tag
    v-if="showAddTagForm"
    :entity-id="entityId"
    @close="closeAddTagForm"
    @update="refreshList"
  />

  <!-- Descendant Tags Modal -->
  <div
    v-if="showDescendantModal"
    class="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
    @click.self="showDescendantModal = false"
  >
    <div class="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[70vh] flex flex-col">
      <div class="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <h3 class="text-sm font-semibold text-gray-900">Descendant Tags</h3>
        <button @click="showDescendantModal = false" class="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
      </div>

      <div class="px-5 py-2 border-b border-gray-100">
        <input
          v-model="descendantSearch"
          placeholder="Filter tags..."
          class="w-full text-sm px-3 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-dark focus:border-transparent"
        />
      </div>

      <div class="overflow-auto flex-1">
        <table class="w-full text-sm">
          <thead class="sticky top-0 bg-gray-50">
            <tr>
              <th
                v-for="col in descendantColumns"
                :key="col.key"
                @click="sortDescendants(col.key)"
                class="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
              >
                {{ col.label }}
                <span v-if="descendantSortKey === col.key" class="ml-0.5">{{ descendantSortAsc ? '↑' : '↓' }}</span>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50">
            <tr v-for="row in filteredDescendantSummary" :key="row.key" class="hover:bg-gray-50">
              <td class="px-5 py-2 text-gray-600">{{ row.tagType }}</td>
              <td class="px-5 py-2">
                <span class="inline-flex items-center gap-1.5">
                  <span class="w-2 h-2 rounded-full inline-block" :style="{ backgroundColor: row.tagColor }"></span>
                  {{ row.tagName }}
                </span>
              </td>
              <td class="px-5 py-2 text-gray-600 tabular-nums">{{ row.count }}</td>
            </tr>
            <tr v-if="filteredDescendantSummary.length === 0">
              <td colspan="3" class="px-5 py-4 text-center text-gray-400 text-xs">No matching tags</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Form_Tag from '@/components/forms/Form_InfoTagRelationship.vue'
import Multi_Button from '@/components/widgetcomponents/Multi_Button.vue'
import TagBadge from "@/components/widgetcomponents/TagBadge.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSitemap, faCirclePlus } from '@fortawesome/free-solid-svg-icons';
import {mapGetters} from 'vuex';

library.add(faSitemap, faCirclePlus);

export default {
  components: {
    FontAwesomeIcon,
    TagBadge,
    Multi_Button,
    Form_Tag,
  },
  props: {
    entityId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      infoTagRelationships: [],
      numReviewed: 0,
      numUnreviewed: 0,
      showAddTagForm: false,
      showDescendantModal: false,
      descendantSearch: '',
      descendantSortKey: 'tagType',
      descendantSortAsc: true,
    }
  },
  computed: {
    ...mapGetters('auth', ['current_user']),

    ownRelationships() {
      const eid = Number(this.entityId);
      return this.infoTagRelationships.filter(r => r.entity === eid);
    },

    descendantRelationships() {
      const eid = Number(this.entityId);
      return this.infoTagRelationships.filter(r => r.entity !== eid);
    },

    aggregatedOwnTags() {
      return this._aggregateTags(this.ownRelationships);
    },

    descendantTagSummary() {
      const tagMap = new Map();
      this.descendantRelationships.forEach(r => {
        const key = `${r.infoTagType || 'none'}_${r.tagName}`;
        if (tagMap.has(key)) {
          tagMap.get(key).count++;
        } else {
          tagMap.set(key, { key, tagType: r.infoTagType, tagName: r.tagName, tagColor: r.UIColor, count: 1 });
        }
      });
      return Array.from(tagMap.values());
    },

    totalDescendantTagCount() {
      return this.descendantRelationships.length;
    },

    descendantColumns() {
      return [
        { key: 'tagType', label: 'Type' },
        { key: 'tagName', label: 'Tag' },
        { key: 'count', label: 'Count' },
      ];
    },

    filteredDescendantSummary() {
      let rows = this.descendantTagSummary;
      if (this.descendantSearch) {
        const q = this.descendantSearch.toLowerCase();
        rows = rows.filter(r => r.tagType?.toLowerCase().includes(q) || r.tagName.toLowerCase().includes(q));
      }
      const key = this.descendantSortKey;
      const asc = this.descendantSortAsc;
      return rows.slice().sort((a, b) => {
        const av = a[key], bv = b[key];
        let cmp;
        if (typeof av === 'number') {
          cmp = av - bv;
        } else {
          cmp = String(av).localeCompare(String(bv));
        }
        if (cmp !== 0) return asc ? cmp : -cmp;
        // Secondary sort: tagName ascending
        if (key !== 'tagName') return String(a.tagName).localeCompare(String(b.tagName));
        return 0;
      });
    },
  },
  methods: {
    _aggregateTags(relationships) {
      const tagMap = new Map();
      relationships.forEach(relationship => {
        const key = `${relationship.infoTagType || 'none'}_${relationship.tagName}`;
        if (tagMap.has(key)) {
          const existing = tagMap.get(key);
          existing.count++;
          existing.relationships.push(relationship);
        } else {
          tagMap.set(key, {
            key,
            tagType: relationship.infoTagType,
            tagName: relationship.tagName,
            tagColor: relationship.UIColor,
            relationships: [relationship],
            count: 1
          });
        }
      });
      return Array.from(tagMap.values());
    },

    sortDescendants(key) {
      if (this.descendantSortKey === key) {
        this.descendantSortAsc = !this.descendantSortAsc;
      } else {
        this.descendantSortKey = key;
        this.descendantSortAsc = key !== 'count';
      }
    },

    openAddTagForm() {
      this.showAddTagForm = true
    },

    closeAddTagForm() {
      this.showAddTagForm = false
      this.refreshList()
    },

    refreshList() {
      this.fetchInfoTagRelationships()
    },

    async fetchInfoTagRelationships() {
      try {
        const params = { entity_id: this.entityId };
        const response = await axios.get('/api/infoTagRelationships/', { params });

        this.infoTagRelationships = response.data;
        this.numReviewed = this.infoTagRelationships.filter(tag => tag.reviewed_status === true).length;
        this.numUnreviewed = this.infoTagRelationships.filter(tag => tag.reviewed_status !== true).length;

      } catch (error) {
        console.error('Error loading info tag relationships:', error);
      }
    },

    async deleteAggregatedTag(aggregatedTag) {
      try {
        await Promise.all(
          aggregatedTag.relationships.map(relationship =>
            axios.delete(`/api/infoTagRelationships/${relationship.id}/`)
          )
        );
        this.fetchInfoTagRelationships();
      } catch (error) {
        console.error('Error deleting aggregated tag relationships:', error);
      }
    },
  },

  mounted() {
    this.fetchInfoTagRelationships()
  },

  watch: {
    entityId() {
      this.fetchInfoTagRelationships();
    },
  }
}
</script>
